export type PinMediaKind =
  | 'image'
  | 'video'
  | 'file'
  | 'provider-file'
  | 'web-link'
  | 'tweet'
  | 'unknown';

/** What a pin is read from: a whole pin, or a search result's preview of one. */
export type PinKindFields = {
  pin_type?: string | null;
  url?: string | null;
  file?: unknown;
};

const IMAGE_URL =
  /^((https?|ftp):)?\/\/.*(jpeg|jpg|png|gif|bmp|avif|heic|webp|tif)/;
const VIDEO_URL = /.(mp4|mov|mkv|mpeg|ogv|webm|wmv|avi|quicktime)$/i;

const LINK_PIN_TYPES = new Set(['github/repo', 'medium/post', 'external_link']);
const IMAGE_PIN_TYPES = new Set([
  'instagram/image',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'application/pdf',
  'external_image_link'
]);
const VIDEO_PIN_TYPES = new Set(['youtube/video', 'vimeo/video']);
const PROVIDER_FILE_PIN_TYPES = new Set([
  'google/application/vnd.google-apps.presentation',
  'google/application/vnd.google-apps.document',
  'google/application/vnd.google-apps.spreadsheet',
  'google/application/vnd.google-apps.file',
  'google/application/vnd.google-apps.folder',
  'google/application/vnd.google-apps.audio',
  'google/application/vnd.google-apps.drawing',
  'google/application/vnd.google-apps.photo',
  'google/application/vnd.google-apps.site',
  'google/application/vnd.google-apps.unknown'
]);

const IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/tif',
  'image/tiff',
  'image/avif',
  'image/bmp',
  'application/pdf'
]);
const VIDEO_MIME_TYPES = new Set([
  'video/mp4',
  'video/mov',
  'video/mkv',
  'video/mpeg',
  'video/ogv',
  'video/webm',
  'video/wmv',
  'video/avi',
  'video/quicktime',
  'video/x-ms-wmv'
]);

function stringField(record: unknown, name: string): string | undefined {
  const value = (record as Record<string, unknown>)[name];
  return typeof value === 'string' ? value : undefined;
}

function fileKind(mimeType: string | undefined): PinMediaKind {
  if (mimeType && IMAGE_MIME_TYPES.has(mimeType)) return 'image';
  if (mimeType && VIDEO_MIME_TYPES.has(mimeType)) return 'video';
  return 'file';
}

function linkKind(url: string | null | undefined): PinMediaKind {
  if (typeof url !== 'string') return 'web-link';
  if (IMAGE_URL.test(url.toLowerCase())) return 'image';
  if (VIDEO_URL.test(url.toLowerCase())) return 'video';
  return 'web-link';
}

/** The kind of a pin, which decides how it is shown and whether it opens as a download. */
export function pinMediaKindOf(pin: PinKindFields): PinMediaKind {
  if (pin.file) return fileKind(stringField(pin.file, 'mime_type'));

  const pinType = pin.pin_type ?? '';
  if (pinType === 'file') return 'unknown';
  if (LINK_PIN_TYPES.has(pinType)) return linkKind(pin.url);
  if (VIDEO_PIN_TYPES.has(pinType)) return 'video';
  if (IMAGE_PIN_TYPES.has(pinType)) return 'image';
  if (PROVIDER_FILE_PIN_TYPES.has(pinType)) return 'provider-file';
  if (pinType === 'twitter/tweet') return 'tweet';
  return 'unknown';
}
